import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Truck, CreditCard } from 'lucide-react';
import axios from 'axios';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { cart, cartTotal, clearCart } = useCart();
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [paypalReady, setPaypalReady] = useState(false);
  const [formData, setFormData] = useState({
    delivery_type: 'pickup',
    delivery_date: '',
    payment_method: 'nopayment',
    notes: ''
  });

  useEffect(() => {
    fetchPaymentInfo();
  }, []);

  const fetchPaymentInfo = async () => {
    try {
      const response = await axios.get(`${API}/payment-info`);
      setPaymentInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch payment info:', error);
    }
  };

  if (!user || !cart.items?.length) {
    navigate('/cart');
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Se pagamento PayPal, non fare submit normale
    if (formData.payment_method === 'paypal') {
      toast.info('Usa il pulsante PayPal per completare il pagamento');
      return;
    }
    
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/orders`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Ordine creato con successo!');
      navigate('/orders');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la creazione dell\'ordine');
    } finally {
      setLoading(false);
    }
  };

  // PayPal: Crea ordine
  const createPayPalOrder = async () => {
    try {
      const response = await axios.post(
        `${API}/paypal/create-order`,
        { amount: cartTotal, currency: 'EUR' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.orderID;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione ordine PayPal');
      throw error;
    }
  };

  // PayPal: Cattura pagamento
  const onPayPalApprove = async (data) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/paypal/capture-order`,
        {
          paypal_order_id: data.orderID,
          delivery_type: formData.delivery_type,
          delivery_date: formData.delivery_date || null,
          notes: formData.notes || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Pagamento completato con successo!');
      await clearCart();
      navigate('/orders');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il pagamento');
    } finally {
      setLoading(false);
    }
  };

  const onPayPalError = (err) => {
    console.error('PayPal error:', err);
    toast.error('Errore durante il pagamento PayPal');
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="container mx-auto px-4 md:px-6 max-w-7xl py-8">
        <h1 className="text-4xl font-extrabold mb-8" data-testid="checkout-title">Checkout</h1>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="bg-white p-6 rounded-2xl border border-gray-200">
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                  <Truck className="h-6 w-6 text-rose-600" />
                  Modalità di Consegna
                </h2>

                <RadioGroup
                  value={formData.delivery_type}
                  onValueChange={(value) => setFormData({ ...formData, delivery_type: value })}
                >
                  <div className="flex items-center space-x-2 p-4 border rounded-lg">
                    <RadioGroupItem value="pickup" id="pickup" data-testid="delivery-pickup" />
                    <Label htmlFor="pickup" className="flex-1 cursor-pointer">
                      <div className="font-semibold">Ritiro in Negozio</div>
                      <div className="text-sm text-gray-600">Ritira il tuo ordine quando vuoi</div>
                    </Label>
                  </div>

                  {paymentInfo?.delivery_enabled && (
                    <div className="flex items-center space-x-2 p-4 border rounded-lg">
                      <RadioGroupItem value="delivery" id="delivery" data-testid="delivery-home" />
                      <Label htmlFor="delivery" className="flex-1 cursor-pointer">
                        <div className="font-semibold">Consegna a Domicilio</div>
                        <div className="text-sm text-gray-600">
                          {paymentInfo?.delivery_day 
                            ? `Consegna disponibile il ${paymentInfo.delivery_day}` 
                            : 'Consegna disponibile un giorno alla settimana'}
                        </div>
                      </Label>
                    </div>
                  )}
                </RadioGroup>

                {formData.delivery_type === 'delivery' && (
                  <div className="mt-4">
                    <Label htmlFor="delivery_date">Data di Consegna Preferita</Label>
                    <Input
                      id="delivery_date"
                      type="date"
                      value={formData.delivery_date}
                      onChange={(e) => setFormData({ ...formData, delivery_date: e.target.value })}
                      data-testid="delivery-date-input"
                    />
                  </div>
                )}
              </div>

              <div className="bg-white p-6 rounded-2xl border border-gray-200">
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                  <CreditCard className="h-6 w-6 text-rose-600" />
                  Metodo di Pagamento
                </h2>

                <RadioGroup
                  value={formData.payment_method}
                  onValueChange={(value) => setFormData({ ...formData, payment_method: value })}
                >
                  <div className="flex items-center space-x-2 p-4 border rounded-lg">
                    <RadioGroupItem value="nopayment" id="nopayment" data-testid="payment-nopayment" />
                    <Label htmlFor="nopayment" className="flex-1 cursor-pointer">
                      <div className="font-semibold">Ordina Senza Pagamento</div>
                      <div className="text-sm text-gray-600">Pagamento al ritiro o alla consegna</div>
                    </Label>
                  </div>

                  {paymentInfo?.paypal_client_id && (
                    <div className="flex items-center space-x-2 p-4 border rounded-lg">
                      <RadioGroupItem value="paypal" id="paypal" data-testid="payment-paypal" />
                      <Label htmlFor="paypal" className="flex-1 cursor-pointer">
                        <div className="font-semibold">PayPal</div>
                        <div className="text-sm text-gray-600">Paga con PayPal o carta di credito</div>
                      </Label>
                    </div>
                  )}

                  {paymentInfo?.iban && (
                    <div className="flex items-center space-x-2 p-4 border rounded-lg">
                      <RadioGroupItem value="iban" id="iban" data-testid="payment-iban" />
                      <Label htmlFor="iban" className="flex-1 cursor-pointer">
                        <div className="font-semibold">Bonifico Bancario (IBAN)</div>
                        <div className="text-sm text-gray-600">Riceverai i dettagli per il bonifico</div>
                      </Label>
                    </div>
                  )}
                </RadioGroup>

                {/* PayPal Button */}
                {formData.payment_method === 'paypal' && paymentInfo?.paypal_client_id && (
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-semibold text-blue-900 mb-3">Paga con PayPal</h4>
                    <PayPalScriptProvider options={{ 
                      clientId: paymentInfo.paypal_client_id,
                      currency: "EUR"
                    }}>
                      <PayPalButtons
                        style={{ layout: "vertical", shape: "rect" }}
                        createOrder={createPayPalOrder}
                        onApprove={onPayPalApprove}
                        onError={onPayPalError}
                        disabled={loading}
                      />
                    </PayPalScriptProvider>
                    <p className="text-xs text-blue-700 mt-2">
                      Verrai reindirizzato a PayPal per completare il pagamento in modo sicuro.
                    </p>
                  </div>
                )}

                {formData.payment_method === 'iban' && paymentInfo?.iban && (
                  <div className="mt-4 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                    <h4 className="font-semibold text-emerald-900 mb-2">Dettagli per il Bonifico:</h4>
                    <div className="space-y-1 text-sm">
                      <p><strong>IBAN:</strong> {paymentInfo.iban}</p>
                      <p><strong>Intestatario:</strong> {paymentInfo.iban_holder}</p>
                      <p><strong>Banca:</strong> {paymentInfo.iban_bank}</p>
                      <p className="text-xs text-emerald-700 mt-2">
                        Riceverai una email con questi dettagli dopo aver confermato l&apos;ordine.
                      </p>
                    </div>
                  </div>
                )}

                <div className="mt-4">
                  <Label htmlFor="notes">Note Aggiuntive (Opzionale)</Label>
                  <Textarea
                    id="notes"
                    placeholder="Inserisci eventuali note per il tuo ordine..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    data-testid="order-notes"
                  />
                </div>
              </div>

              {/* Mostra pulsante solo se NON è PayPal */}
              {formData.payment_method !== 'paypal' && (
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-rose-600 hover:bg-rose-700 text-white rounded-full font-bold py-6"
                  data-testid="place-order-button"
                >
                  {loading ? 'Elaborazione...' : 'Conferma Ordine'}
                </Button>
              )}
            </form>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-orange-50 p-6 rounded-2xl border border-gray-200 sticky top-24">
              <h2 className="text-2xl font-bold mb-4">Riepilogo</h2>

              <div className="space-y-2 mb-4">
                {cart.items.map((item) => (
                  <div key={item.product_id} className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      {item.name} x {item.quantity}
                    </span>
                    <span className="font-semibold">€{(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <div className="flex justify-between text-xl font-bold pt-4 border-t">
                <span>Totale</span>
                <span className="text-emerald-600" data-testid="checkout-total">€{cartTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}